import React from "react";
import { Helmet } from "react-helmet";
import Footer from "../Components/Footer";
import { LegalLayout, Section, Paragraph, BulletList } from "../Components/Legal/LegalLayout";

export default function RefundCancellationPolicy() {
  return (
    <div>
      <Helmet>
        <title>Refund & Cancellation Policy | GroHair & GloSkin Dindigul</title>
        <meta
          name="description"
          content="Read the Refund and Cancellation Policy for consultations, treatments and packages at GroHair & GloSkin Dindigul."
        />
        <meta property="og:title" content="Refund & Cancellation Policy | GroHair & GloSkin Dindigul" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://adgrohairgloskindindigul.in/refund-cancellation-policy" />
      </Helmet>

      <LegalLayout
        title="Return and Refund Policy"
        lastUpdated="August 28, 2026"
        intro={
          <>
            Welcome to Advanced GroHair & GloSkin – Dindigul. We are a hair and skin clinic offering
            consultations and aesthetic and clinical treatment services. This Return and Refund
            Policy explains how payments, cancellations, rescheduling and refunds are handled for
            services booked at our clinic or through our website,{" "}
            <a
              href="https://adgrohairgloskindindigul.in/"
              style={{ color: "#e3242b" }}
            >
              https://adgrohairgloskindindigul.in/
            </a>
            .
            <br />
            <br />
            We believe in transparency. This policy is written in plain language so that you
            understand clearly, before you pay, what is refundable and what is not. By booking a
            consultation, purchasing a treatment, enrolling in a treatment package, or making any
            payment to Advanced GroHair & GloSkin – Dindigul, you confirm that you have read,
            understood and accepted the terms set out below.
            <br />
            <br />
            <strong>Please note:</strong> Our services are personal, professional healthcare and
            aesthetic services delivered by trained practitioners. Unlike physical goods, a service
            that has already been performed cannot be returned. For this reason, this policy
            distinguishes carefully between payments made in advance and payments made for services
            already delivered.
          </>
        }
      >
        <Section heading="Consultation and Appointment Fees">
          <BulletList
            items={[
              "Consultation fees cover the professional time of our doctors, trichologists, dermatology consultants and clinical staff, along with any diagnostic assessment carried out during your visit.",
              "Consultation fees become non-refundable once the consultation has taken place, regardless of whether you choose to proceed with any recommended treatment.",
              "If you cancel or reschedule a consultation within the notice period described under Cancellation and Rescheduling below, your consultation fee may be adjusted against a future appointment or refunded, subject to review.",
              'If you do not attend a booked consultation without informing the clinic in advance (a "no-show"), the consultation fee may be forfeited.',
              "Where the clinic offers a complimentary or promotional consultation, no refund arises, as no fee has been collected.",
              "In some cases, at the clinic's discretion, the consultation fee may be adjusted against the cost of a treatment you subsequently book. Where such an adjustment is offered, it will be confirmed to you in writing at the time of booking.",
            ]}
          />
        </Section>

        <Section heading="Treatment and Service Payments">
          <BulletList
            items={[
              "All treatment charges are communicated to you before the treatment begins. You will be informed of the applicable cost, the number of sessions advised, and the payment terms.",
              "Payment may be collected in full in advance, in instalments, or on a per-session basis, depending on the treatment plan agreed with you.",
              "We accept payments through the modes made available at our clinic and, where applicable, through our website. All payments are in Indian Rupees (₹) and are subject to applicable taxes.",
              "A payment receipt or invoice will be issued for every payment received. Please retain your receipt, as it is required for processing any refund request.",
              "Once a treatment session has been performed, the amount paid for that specific session is treated as consumed and is not refundable.",
              "Where a treatment plan has been paid for in advance and only part of it has been used, refund eligibility for the unused portion is dealt with under Refund Eligibility and Treatment Packages below.",
            ]}
          />
        </Section>

        <Section heading="Refund Eligibility">
          <Paragraph>
            Refunds are considered on a case-by-case basis. You may be eligible to request a refund
            in situations including, but not limited to:
          </Paragraph>
          <BulletList
            items={[
              "Services not yet availed — you have paid in advance for a treatment or session that has not yet been performed.",
              "Cancellation by the clinic — the clinic cancels a scheduled treatment and is unable to offer you a mutually acceptable alternative date.",
              "Medical unsuitability — following clinical assessment, our practitioners advise that a paid treatment is not suitable for you and should not proceed (see Medical or Treatment Suitability below).",
              "Duplicate or excess payment — a payment has been collected twice, or an amount higher than the agreed charge has been debited.",
              "Payment or technical error — an online transaction has been processed incorrectly.",
            ]}
          />

          <Paragraph><strong>How to request a refund</strong></Paragraph>
          <BulletList
            items={[
              "Submit your request in writing to the clinic, either in person at our reception or through the contact details given below.",
              "Include your full name, registered mobile number, treatment or service details, date of payment, amount paid, and your reason for the request.",
              "Attach or produce your original payment receipt or invoice.",
            ]}
          />

          <Paragraph><strong>Review process</strong></Paragraph>
          <BulletList
            items={[
              "Every refund request is reviewed by the clinic management along with the treating practitioner, where clinically relevant.",
              "The review takes into account the nature of the service, the number of sessions already delivered, consumables and clinical materials already used or reserved for you, the notice given, and any third-party or payment-gateway costs already incurred.",
              "All refunds, where applicable, are subject to review based on the specific circumstances of the service or treatment concerned. Approval of a refund in one instance does not create an entitlement in any other instance.",
              "The outcome of the review will be communicated to you in writing.",
            ]}
          />
        </Section>

        <Section heading="Non-Refundable Services">
          <Paragraph>The following are generally not eligible for refund:</Paragraph>
          <BulletList
            items={[
              "Consultations, assessments, diagnostic tests and evaluations that have already been carried out.",
              "Treatment sessions that have already been performed, whether in full or in part.",
              "Services where single-use consumables, medications, injectables, or personalised clinical materials have already been opened, prepared or used for you.",
              "Customised or personalised treatment protocols and formulations prepared specifically for you.",
              "Promotional offers, discounted packages, seasonal camps or festival offers that are expressly stated to be non-refundable at the time of purchase.",
              "Complimentary, free or bundled sessions provided as part of an offer or package.",
              "Gift vouchers, prepaid cards and referral credits, unless expressly stated otherwise at the time of issue.",
              'Appointments missed without prior intimation ("no-shows").',
              "Sessions lapsed due to non-attendance beyond the validity period of a package (see Treatment Packages and Advance Payments below).",
              "Dissatisfaction based solely on the pace, extent or visibility of results, where the treatment has been delivered as agreed and in accordance with accepted clinical practice.",
              "Discontinuation of a treatment course by the client for personal reasons, in respect of the sessions already completed.",
            ]}
          />
          <Paragraph>
            <strong>A note on results.</strong> Hair and skin treatments are biological in nature.
            Outcomes depend on individual factors and cannot be guaranteed. Please read Medical or
            Treatment Suitability below carefully. Payment for our services is payment for
            professional clinical care, expertise, time and materials — not for a specific or
            assured result.
          </Paragraph>
        </Section>

        <Section heading="Cancellation and Rescheduling Policy">
          <Paragraph>
            We understand that plans change. We ask only that you inform us early, so that your
            appointment slot can be offered to another client.
          </Paragraph>

          <Paragraph><strong>Cancellation or rescheduling by you</strong></Paragraph>
          <BulletList
            items={[
              "Please inform the clinic at least 24 hours before your scheduled appointment.",
              "Notice may be given by telephone, WhatsApp, email, or in person at our reception.",
              "Where adequate notice is given, we will make every reasonable effort to reschedule your appointment to a date and time convenient to you, at no additional cost.",
              "Where notice is given late, or where you do not attend without informing us, a nominal cancellation or administrative charge may apply, as informed to you at the time of booking, or the session may be treated as consumed.",
              "Repeated late cancellations or no-shows may result in the clinic requiring advance confirmation or advance payment for future bookings.",
            ]}
          />

          <Paragraph><strong>Rescheduling limits</strong></Paragraph>
          <BulletList
            items={[
              "An appointment may ordinarily be rescheduled a reasonable number of times. Beyond this, the clinic may require a fresh booking.",
              "Rescheduling is always subject to the availability of the treating practitioner and clinic slots.",
            ]}
          />

          <Paragraph><strong>Cancellation by the clinic</strong></Paragraph>
          <Paragraph>
            We may occasionally need to cancel or postpone an appointment — for example, due to
            practitioner unavailability, equipment maintenance, power failure, or other operational
            reasons. In such cases:
          </Paragraph>
          <BulletList
            items={[
              "You will be informed at the earliest possible opportunity.",
              "You will be offered a rescheduled appointment at your convenience, at no additional cost.",
              "If a suitable alternative cannot be arranged, the amount paid for that unperformed session will be refunded or retained as clinic credit, as you prefer.",
            ]}
          />
        </Section>

        <Section heading="Refund Processing Time">
          <BulletList
            items={[
              "Approved refund requests are ordinarily processed within 7–10 working days from the date of written approval.",
              "Refunds are made only to the original mode and source of payment. For example, a payment made by card will be refunded to the same card; a UPI payment will be refunded to the same UPI account.",
              "Where the original payment mode is no longer available or cannot be used, an alternative mode will be arranged after verification of your identity and payment records.",
              "Once processed from our end, the credit of funds to your account depends on your bank, card issuer or payment gateway. This typically takes an additional 5–7 working days, and this timeline is outside the clinic's control.",
              "Applicable taxes, statutory deductions, payment-gateway charges and bank charges may be deducted from the refund amount where these have already been incurred and are non-recoverable by the clinic.",
              "You will be provided with a refund confirmation or acknowledgement once the refund has been initiated.",
            ]}
          />
        </Section>

        <Section heading="Treatment Packages and Advance Payments">
          <Paragraph>
            Many of our hair and skin treatments are delivered as multi-session packages. The
            following terms apply to packages and advance payments:
          </Paragraph>
          <BulletList
            items={[
              "Package pricing is offered at a concessional rate on the understanding that the full course will be completed. This discount is the reason package terms differ from single-session terms.",
              "Each package has a validity period, which will be communicated to you at the time of purchase. Sessions not availed within the validity period will lapse and will not be refundable, unless an extension has been approved in writing by the clinic.",
              "If you choose to discontinue a package mid-way, sessions already availed (including any complimentary or bonus sessions used) will be charged at the applicable single-session (non-package) rate, not the discounted package rate. The balance amount, if any, may be considered for refund or converted to clinic credit, subject to review and to a reasonable administrative charge.",
              "Advance payments made to block or reserve appointment slots, equipment time or imported consumables may be partly or wholly non-refundable, as such costs are committed by the clinic in advance. The applicable terms will be explained to you before you make the payment.",
              "Packages are non-transferable to another person unless expressly approved in writing by the clinic management.",
              "Where a package is discontinued on the written advice of our treating practitioner for medical reasons, refund of the unused portion will be considered on a pro-rata basis under Medical or Treatment Suitability below.",
              "Where a package is paid for in instalments and is discontinued, the clinic reserves the right to recover the value of sessions already delivered at single-session rates before processing any balance.",
            ]}
          />
        </Section>

        <Section heading="Medical or Treatment Suitability">
          <Paragraph>
            Your safety comes before your treatment plan. Please read this section carefully.
          </Paragraph>
          <BulletList
            items={[
              "All treatments are provided only after a clinical assessment of your suitability by our qualified practitioners.",
              "You are requested to disclose complete and accurate information about your medical history, existing conditions, allergies, current medications, pregnancy or planned pregnancy, and any previous treatments. Refunds may be affected where a material fact has not been disclosed to us.",
              "If, during assessment or at any point during a treatment course, our practitioners determine that a treatment is not medically suitable for you or should be discontinued, we will inform you and stop the treatment. In such cases, the amount paid for the unperformed portion will be considered for refund or clinic credit, on a pro-rata basis.",
              "Individual results vary. Response to hair and skin treatments depends on factors including age, genetics, hormonal status, underlying medical conditions, skin and scalp type, lifestyle, nutrition, adherence to the advised protocol, and aftercare compliance.",
              "No specific outcome, timeline, or degree of improvement is guaranteed or promised, either verbally or in writing, by the clinic or any of its staff. Any images, testimonials or case references shared are illustrative of individual experiences and are not a representation of the results you will obtain.",
              "Certain treatments may produce temporary side effects such as redness, mild swelling, dryness, irritation or sensitivity. These are recognised and expected responses in clinical practice and do not, by themselves, constitute grounds for a refund.",
              "Following the aftercare instructions, prescribed medication schedule and review appointments advised by our practitioners is essential. Refund requests arising from non-adherence to advised protocols will not ordinarily be accepted.",
              "If you experience any unexpected reaction or discomfort, please contact the clinic immediately so that we can review and support you clinically.",
            ]}
          />
        </Section>

        <Section heading="Products, If Applicable">
          <Paragraph>
            Where the clinic sells or dispenses hair care, skin care or supplementary products:
          </Paragraph>
          <BulletList
            items={[
              "Products may be returned or exchanged only if they are unopened, unused, and in their original sealed packaging, with the original invoice, and within 7 days of purchase.",
              "For reasons of hygiene, safety and regulatory compliance, opened or used products cannot be returned or exchanged.",
              "Prescription medicines and dispensed pharmaceutical items are non-returnable and non-refundable, in line with standard practice for medicinal products.",
              "If a product is received in a damaged condition, or is found to have expired or to be defective at the time of purchase, please report this to the clinic within 3 days. On verification, we will replace the product or process a refund.",
              "Products purchased under a promotional offer, combo or bundled scheme may carry different return terms, which will be stated at the point of sale.",
              "Product returns, where accepted, are refunded to the original payment mode in accordance with Refund Processing Time above.",
            ]}
          />
        </Section>

        <Section heading="Exceptional Circumstances">
          <Paragraph>
            We recognise that life does not always follow a schedule. The clinic may, at its sole
            discretion, relax the terms of this policy in genuine and exceptional situations, such
            as:
          </Paragraph>
          <BulletList
            items={[
              "Serious illness, hospitalisation or medical emergency affecting the client, supported by documentation.",
              "Bereavement in the immediate family.",
              "Permanent relocation of the client to another city or country, supported by documentation.",
              "Pregnancy, or a change in medical status that makes continuing treatment inadvisable.",
              "Natural calamity, civil disruption, public health restrictions, or other events beyond reasonable control that prevent the clinic from delivering, or the client from receiving, the scheduled service.",
            ]}
          />
          <Paragraph>
            In such cases, the clinic may offer a rescheduled appointment, an extension of package
            validity, clinic credit, transfer of the balance to an immediate family member, or a
            partial or full refund — whichever the clinic considers fair in the circumstances. Any
            relaxation granted under this section is made on a goodwill basis, is decided case by
            case, and does not set a precedent or create an entitlement for any other client or any
            future request.
          </Paragraph>
        </Section>

        <Section heading="Changes to the Policy">
          <BulletList
            items={[
              "Advanced GroHair & GloSkin – Dindigul reserves the right to amend, update or revise this Return and Refund Policy at any time, in order to reflect changes in our services, operational practices, or applicable law.",
              <>
                Any revised policy will be published on our website at{" "}
                <a href="https://adgrohairgloskindindigul.in/" style={{ color: "#e3242b" }}>
                  https://adgrohairgloskindindigul.in/
                </a>{" "}
                with an updated "Last Updated" date.
              </>,
              "The version of the policy in force on the date of your payment will apply to that transaction.",
              "We encourage you to review this page periodically. Continued use of our services after a revision indicates your acceptance of the updated policy.",
              "This policy is governed by the laws of India. Any dispute arising in connection with it shall be subject to the jurisdiction of the courts at Dindigul, Tamil Nadu.",
              "Nothing in this policy limits any right available to you under applicable Indian consumer protection law.",
            ]}
          />
        </Section>

        <Section heading="Contact Information">
          <Paragraph>
            If you have any questions about this policy, or wish to submit a refund, cancellation or
            rescheduling request, please contact us. We aim to respond to every written request
            promptly and fairly.
          </Paragraph>
          <Paragraph><strong>Advanced GroHair & GloSkin – Dindigul</strong></Paragraph>
          <Paragraph>
            1st floor, 2, UTHCHAV MALL, 6, Trichy - Dindigul Rd,
            <br />
            near City Hospital, Gandhiji Nagar, Cooperative Nagar,
            <br />
            Dindigul, Tamil Nadu 624001
          </Paragraph>
          <BulletList
            items={[
              <>
                Website:{" "}
                <a href="https://adgrohairgloskindindigul.in/" style={{ color: "#e3242b" }}>
                  https://adgrohairgloskindindigul.in/
                </a>
              </>,
              "Phone: +91 082701 56789",
              "Email: adgrohairdgl@gmail.com",
              "Clinic Hours: Daily, 10:00 AM – 8:00 PM",
            ]}
          />
        </Section>
      </LegalLayout>

      <Footer />
    </div>
  );
}
